/**
 * Générateur — Il utilise « Répéter … fois », « Répéter jusqu'à … » et les
 * instructions conditionnelles (figures, calculs, simulations).
 * Attendu ID : 252 | Niveau : 3ème | Domaine : Algorithmique (d21, O3 niv. 3)
 *
 * Source doc prof (A5S1.Ex.4, Créés n°20-21). Plan auto-validé 2026-07-18 :
 * « reste » (le test de divisibilité : condition = 0 / que dit le script,
 * QCM — croisement Nombres 3e), « balle » (« répéter jusqu'à » : la balle
 * qui rebondit, dérouler la boucle et compter les tours), « des » (jusqu'au
 * premier six : bornes de l'aléatoire et affichages possibles — aucune
 * borne certaine).
 */

import { randomChoice, randInt, tex, makeQcm, makeTexteTrou } from '../../../utils';
import { imgElement, svgScript } from '../../4eme/algorithmique/algo';

const ATTENDU_ID = 252;
const L_RESTE = 'Le reste dans le script (divisibilité)';
const L_BALLE = 'La balle qui rebondit (répéter jusqu\'à)';
const L_DES = 'Jusqu\'au premier six';
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const fr = (x) => String(x).replace('.', ',');

// ═════════ « reste » — QCM ═════════

const genResteQcm = () => {
    const d = randomChoice([3, 4, 6, 7, 9, 11, 13]);
    if (randInt(0, 1) === 1) {
        const correct = 'reste = 0';
        const payload = makeQcm([correct, 'reste = 1', `reste = ${d}`, 'reste > 0'], correct,
            `<p><code>demander N ; si (le RESTE de la division de N par ${d}) … alors dire `
            + `« ${d} divise N » sinon dire « ${d} ne divise pas N »</code></p>`
            // « quand la division euclidienne tombe juste » est la définition
            // même que la carte fait traduire en condition de script.
            + '<p><b>Quelle condition écrire à la place des pointillés ?</b></p>',
            opts(L_RESTE));
        payload._meta.params = { variante: 'condition', d };
        return payload;
    }
    const divise = randInt(0, 1) === 1;
    const q = randInt(7, 30);
    const N = d * q + (divise ? 0 : randInt(1, d - 1));
    const correct = N % d === 0 ? `« ${d} divise N »` : `« ${d} ne divise pas N »`;
    const payload = makeQcm([`« ${d} divise N »`, `« ${d} ne divise pas N »`, 'Rien : le script plante'], correct,
        `<p><code>demander N ; si (le RESTE de la division de N par ${d}) = 0 alors dire `
        + `« ${d} divise N » sinon dire « ${d} ne divise pas N »</code></p>`
        + `<p>On entre N = ${N}.</p>`
        // « Pose la division euclidienne » donnait la démarche.
        + '<p><b>Que dit le script ?</b></p>',
        opts(L_RESTE));
    payload._meta.params = { variante: 'execution', d, N };
    return payload;
};

// ═════════ « balle » — répéter jusqu'à ═════════

const genBalleTrous = () => {
    // Hauteur × f à chaque rebond, f exact ; on s'arrête sous le seuil.
    let h0; let fNum; let fDen; let fAff; let seuil; let h1; let h2; let rebonds;
    for (let essai = 0; essai < 60; essai++) {
        const fmt = randomChoice([[3, 4, '0,75'], [1, 2, '0,5'], [4, 5, '0,8'], [3, 5, '0,6']]);
        [fNum, fDen, fAff] = fmt;
        h0 = randomChoice([80, 100, 120, 160, 200]);
        seuil = randomChoice([10, 20]);
        h1 = (h0 * fNum) / fDen;
        h2 = (h1 * fNum) / fDen;
        if (!Number.isInteger(h1) || h1 === h2 || h1 === h0) continue;
        rebonds = 0;
        let h = h0;
        while (h >= seuil && rebonds < 40) { h = (h * fNum) / fDen; rebonds += 1; }
        if (rebonds >= 3 && rebonds !== h1 && rebonds !== Math.round(h2)) break;
    }
    const h2Aff = fr(Math.round(h2 * 100) / 100);
    const phrase = `Facteur : {{${fAff}}} ; après 1 rebond : {{${h1}}} cm ; après 2 rebonds : {{${h2Aff}}} cm ; le programme dit {{${rebonds}}}.`;
    const payload = makeTexteTrou(phrase,
        `<p>On lâche une balle de ${h0} cm de haut ; à chaque rebond elle remonte `
        + `${fNum === 1 ? 'à la moitié' : `aux ${tex(`\\dfrac{${fNum}}{${fDen}}`)}`} de la hauteur précédente.</p>`
        + `<p><code>mettre Hauteur à ${h0} ; mettre Rebonds à 0 ; répéter JUSQU'À (Hauteur &lt; ${seuil}) `
        + '[mettre Hauteur à Hauteur × … ; ajouter 1 à Rebonds] ; dire Rebonds</code></p>'
        + '<p><b>Complète le facteur, les deux premières hauteurs, puis fais tourner la boucle jusqu\'au '
        + 'bout : quel nombre le programme dit-il ?</b> Écris le facteur en écriture décimale.</p>',
        { ...opts(L_BALLE), mode: 'saisie' });
    payload.elements_additionnels = imgElement(svgScript([
        { t: 'hat', txt: 'quand le drapeau est cliqué' },
        { t: 'variable', txt: `mettre Hauteur à ${h0}` },
        { t: 'variable', txt: 'mettre Rebonds à 0' },
        { t: 'repeat', jusqua: `Hauteur &lt; ${seuil}`, body: [
            { t: 'variable', txt: 'mettre Hauteur à Hauteur × …' },
            { t: 'variable', txt: 'ajouter 1 à Rebonds' },
        ] },
        { t: 'looks', txt: 'dire Rebonds' },
    ]));
    payload._meta.params = { h0, fNum, fDen, seuil, h1, h2, rebonds };
    return payload;
};

// ═════════ « des » — QCM ═════════

const genDesQcm = () => {
    const prenom = randomChoice(['Amina', 'Lucas', 'Inès', 'Rayan']);
    const variante = randInt(0, 2);
    const contexte = '<p><code>mettre Lancers à 0 ; répéter JUSQU\'À (Dé = 6) [mettre Dé à '
        + '(nombre aléatoire entre 1 et 6) ; ajouter 1 à Lancers] ; dire Lancers</code></p>'
        + `<p>${prenom} exécute plusieurs fois ce programme.</p>`;
    let question; let correct; let choix;
    if (variante === 0) {
        question = 'Le programme peut-il dire 1 ?';
        correct = 'Oui : il suffit d\'obtenir 6 dès le premier lancer';
        choix = [correct, 'Non : il faut toujours au moins deux lancers', 'Non : il dit toujours 6'];
    } else if (variante === 1) {
        const grand = randomChoice([20, 30, 50]);
        question = `Le programme peut-il dire ${grand} ?`;
        correct = 'Oui : c\'est rare, mais rien ne garantit un 6 avant';
        choix = [correct, 'Non : on obtient forcément un 6 en 6 lancers au plus', 'Non : le script s\'arrête tout seul à 10'];
    } else {
        question = 'Existe-t-il un nombre MAXIMAL que le programme ne peut jamais dépasser ?';
        correct = 'Non : aucune borne n\'est certaine, la boucle « jusqu\'à » peut durer longtemps';
        choix = [correct, 'Oui : 6, comme les faces du dé', 'Oui : 36, car 6 × 6 = 36'];
    }
    const payload = makeQcm(choix, correct, `${contexte}<p><b>${question}</b></p>`, opts(L_DES));
    payload._meta.params = { variante, prenom };
    return payload;
};

const GENERATORS = {
    reste_qcm_unique: genResteQcm,
    balle_texte_trou_saisie: genBalleTrous,
    des_qcm_unique: genDesQcm,
};
const FORMAT_LABELS = { qcm_unique: 'QCM', texte_trou_saisie: 'Saisie trous' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));
export const CATEGORIES = [
    { id: 'reste', label: L_RESTE, formatsList: buildFormats('reste') },
    { id: 'balle', label: L_BALLE, formatsList: buildFormats('balle') },
    { id: 'des', label: L_DES, formatsList: buildFormats('des') },
];
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
